import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Trash2 } from 'lucide-react';
import { ApiError, api } from '../../lib/api.js';
import { ago } from '../../lib/format.js';
import {
  Badge,
  Button,
  Callout,
  Checkbox,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Field,
  Input,
  Panel,
  Spinner,
  useToast,
} from '../../ui/index.js';
import { ConfirmAction, NewButton } from './forms.js';
import { useConfigWrite } from './useConfigWrite.js';

/**
 * Credentials for the systems that talk to this desk without a person behind
 * them — the mail assistant, n8n, anything else holding a key.
 *
 * THE SECRET IS SHOWN ONCE. Only its hash is stored, so there is no second
 * chance and no "show it again" to build. That single fact drives the whole
 * design of the create flow below: the dialog does not close on success, the
 * key is the only thing in it, and dismissing it takes a deliberate act rather
 * than the Escape key somebody presses out of habit.
 *
 * What the screen must not become is a list of opaque strings. `ticket.state`
 * means nothing to the person deciding whether an integration should have it,
 * and the safe-feeling guess — tick everything — is the one that hands a mail
 * bot the ability to delete tickets. So capabilities arrive from the server in
 * plain language, grouped, and already filtered to what this administrator may
 * actually confer.
 */

interface KeyRow {
  id: string;
  name: string;
  key_prefix: string;
  capabilities: string[];
  created_at: string;
  created_by_name: string | null;
  last_used_at: string | null;
  revoked_at: string | null;
}

interface Assignable {
  capability: string;
  label: string;
  detail?: string;
  group: string;
}

interface Preset {
  key: string;
  label: string;
  detail: string;
  capabilities: string[];
  /** False when this administrator cannot confer every part of the preset. */
  complete: boolean;
}

interface KeysResponse {
  keys: KeyRow[];
  assignable: Assignable[];
  presets: Preset[];
}

export function IntegrationsTab({ tenant }: { tenant: string }) {
  const write = useConfigWrite(tenant);
  const q = useQuery({
    queryKey: ['admin-keys', tenant],
    queryFn: () => api.get<KeysResponse>(`/api/v1/tenants/${tenant}/admin/keys`),
    enabled: Boolean(tenant),
  });

  if (q.isLoading) return <Spinner />;
  if (q.error) return <p className="text-xs text-ink-tertiary">Keys could not be loaded.</p>;

  const keys = q.data?.keys ?? [];
  const active = keys.filter((k) => !k.revoked_at);
  const unused = active.filter((k) => !k.last_used_at);

  return (
    <Panel
      title="Integration keys"
      subtitle={
        active.length === 0
          ? 'No integration currently holds a credential for this group'
          : `${active.length} active · ${keys.length - active.length} revoked`
      }
      action={
        <NewKeyDialog
          tenant={tenant}
          assignable={q.data?.assignable ?? []}
          presets={q.data?.presets ?? []}
          trigger={<NewButton>New key</NewButton>}
        />
      }
      footer={
        <p className="text-2xs leading-relaxed text-ink-tertiary">
          A key is bound to <strong className="text-ink-secondary">this group only</strong> and to
          the capabilities listed against it. It cannot inherit anybody&rsquo;s role, and no key can
          be given the ability to create keys, grant access, or act as a person — so the strongest
          key that can exist is weaker than whoever created it.
        </p>
      }
    >
      {/* A key nobody has ever used is either a failed integration or a
          forgotten one, and both are worth revoking. Named here because
          scanning a "last used" column for blanks is the sort of thing people
          intend to do and never do. */}
      {unused.length > 0 && (
        <Callout tone="warn" className="mb-4">
          {unused.length === 1 ? (
            <>
              <strong>{unused[0]!.name}</strong> has never been used. If the integration it was
              made for is working, it is using a different key.
            </>
          ) : (
            <>
              <strong>{unused.length} keys have never been used.</strong> Each one is a live
              credential doing nothing — {unused.map((k) => k.name).join(', ')}.
            </>
          )}
        </Callout>
      )}

      {keys.length === 0 ? (
        <div className="py-6 text-center">
          <KeyRound aria-hidden className="mx-auto h-5 w-5 text-ink-tertiary" />
          <p className="mt-2 text-xs text-ink-secondary">
            Nothing machine-driven reaches this desk yet.
          </p>
          <p className="mt-1 text-2xs text-ink-tertiary">
            The mail assistant needs one of these before it can file a ticket.
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-line-subtle">
          {keys.map((k) => (
            <KeyLine key={k.id} tenant={tenant} row={k} write={write} />
          ))}
        </ul>
      )}
    </Panel>
  );
}

function KeyLine({
  tenant,
  row,
  write,
}: {
  tenant: string;
  row: KeyRow;
  write: ReturnType<typeof useConfigWrite>;
}) {
  const revoked = Boolean(row.revoked_at);
  return (
    <li className="group flex items-start gap-3 py-2.5 first:pt-0">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className={`truncate text-sm ${revoked ? 'text-ink-tertiary line-through' : 'text-ink'}`}>
            {row.name}
          </p>
          <code className="nums shrink-0 rounded-xs bg-surface-sunken px-1 py-0.5 text-2xs text-ink-tertiary">
            {row.key_prefix}…
          </code>
        </div>
        <p className="mt-0.5 truncate text-2xs text-ink-tertiary">
          {row.capabilities.length}{' '}
          {row.capabilities.length === 1 ? 'capability' : 'capabilities'}
          {row.created_by_name ? ` · created by ${row.created_by_name}` : ''}
        </p>
      </div>

      <Badge tone={revoked ? 'neutral' : row.last_used_at ? 'ok' : 'warn'}>
        {revoked ? 'revoked' : row.last_used_at ? `used ${ago(row.last_used_at)}` : 'never used'}
      </Badge>

      <span className="w-8 shrink-0">
        {!revoked && (
          <span className="opacity-40 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
            <ConfirmAction
              trigger={
                <Button variant="ghost" size="sm" iconOnly aria-label={`Revoke ${row.name}`}>
                  <Trash2 aria-hidden className="h-3.5 w-3.5" />
                </Button>
              }
              title="Revoke this key?"
              body={
                <>
                  Anything still using <strong>{row.name}</strong> stops working immediately and
                  starts getting <strong>401 invalid API key</strong>. There is no undo — a
                  replacement is a new key with a new secret, so make sure you can reach whoever
                  configured the integration.
                  {row.last_used_at && (
                    <> It was last used {ago(row.last_used_at)}, so something is still holding it.</>
                  )}
                </>
              }
              confirmLabel="Revoke"
              pending={write.isPending}
              onConfirm={() =>
                write.mutateAsync({
                  method: 'del',
                  path: `/api/v1/tenants/${tenant}/admin/keys/${row.id}`,
                  done: `${row.name} revoked`,
                })
              }
            />
          </span>
        )}
      </span>
    </li>
  );
}

/**
 * The one screen in the application that shows something it can never show
 * again.
 *
 * Exported so it can be rendered on its own in a test: it is reachable in the
 * running app only after a successful write, which is the worst possible place
 * to discover that the secret did not render or the copy button was missing.
 */
export function KeyRevealed({
  secret,
  copied,
  onCopy,
  onDone,
}: {
  secret: string;
  copied: boolean;
  onCopy: () => void;
  onDone: () => void;
}) {
  return (
    <div className="space-y-3">
      <Callout tone="warn">
        <strong>This is the only time this key will be shown.</strong> Only its hash is stored, so
        nobody — including a platform administrator — can retrieve it later. If it is lost, the
        only repair is to revoke this key and issue another.
      </Callout>

      <div className="flex items-stretch gap-2">
        <code className="min-w-0 flex-1 select-all break-all rounded-xs border border-line-subtle bg-surface-sunken px-2 py-2 text-2xs leading-relaxed text-ink">
          {secret}
        </code>
        <Button
          type="button"
          variant={copied ? 'secondary' : 'primary'}
          onClick={onCopy}
          aria-label="Copy the key to the clipboard"
        >
          {copied ? (
            <Check aria-hidden className="h-3.5 w-3.5" />
          ) : (
            <Copy aria-hidden className="h-3.5 w-3.5" />
          )}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>

      <p className="text-2xs leading-relaxed text-ink-tertiary">
        Send it as the <code className="text-ink-secondary">X-API-Key</code> header. Store it where
        the integration reads its configuration — never in a repository.
      </p>

      <DialogFooter>
        {/* No Cancel and no DialogClose: every way out of this panel is the
            one button, so leaving is a decision rather than a reflex. */}
        <Button type="button" variant={copied ? 'primary' : 'ghost'} onClick={onDone}>
          {copied ? 'Done' : 'Close without copying'}
        </Button>
      </DialogFooter>
    </div>
  );
}

/**
 * Create, then reveal.
 *
 * Deliberately NOT the shared `FormDialog`, which closes as soon as the write
 * lands. Here the write landing is the moment the only copy of the secret
 * comes into existence, and closing on it would destroy the thing the user
 * came for.
 */
function NewKeyDialog({
  tenant,
  assignable,
  presets,
  trigger,
}: {
  tenant: string;
  assignable: Assignable[];
  presets: Preset[];
  trigger: React.ReactNode;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);
  const [secret, setSecret] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  /**
   * Grouped, and in a deliberate order rather than the order the capability
   * list happens to declare them.
   *
   * It runs from least to most consequential, so somebody scrolling stops
   * getting what they came for before they reach the things that change
   * Egnyte. Declaration order put "Writing" first and buried "list the
   * group's tickets" below the fold of the scroll box.
   */
  const groups = useMemo(() => {
    const ORDER = ['Reading', 'Writing', 'Knowledge', 'Moving work', 'Real systems', 'Administration'];
    const out = new Map<string, Assignable[]>();
    for (const a of assignable) out.set(a.group, [...(out.get(a.group) ?? []), a]);
    return [...out].sort(([a], [b]) => {
      const ia = ORDER.indexOf(a);
      const ib = ORDER.indexOf(b);
      // An unknown group sorts last rather than first: a group added to the
      // vocabulary and not to this list must not silently lead the dialog.
      return (ia === -1 ? ORDER.length : ia) - (ib === -1 ? ORDER.length : ib);
    });
  }, [assignable]);

  const reset = () => {
    setName('');
    setChosen([]);
    setSecret(null);
    setCopied(false);
  };

  const create = useMutation({
    mutationFn: () =>
      api.post<{ key: string }>(`/api/v1/tenants/${tenant}/admin/keys`, {
        name: name.trim(),
        capabilities: chosen,
      }),
    onSuccess: (data) => {
      setSecret(data.key);
      void qc.invalidateQueries({ queryKey: ['admin-keys', tenant] });
    },
    onError: (err: unknown) => {
      // The server's refusals are the useful part — "you cannot give a key
      // carry out a change to a real system — you do not hold that yourself"
      // tells the administrator something true about their own access.
      const msg = err instanceof ApiError ? err.message : 'The key could not be created';
      const fields = err instanceof ApiError ? err.fields : undefined;
      toast.error(
        msg,
        fields && fields.length > 0 ? fields.map((f) => f.message).join(' · ') : undefined,
      );
    },
  });

  const toggle = (c: string) =>
    setChosen((prev) => (prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]));

  const copy = async () => {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
    } catch {
      // A blocked clipboard is not an error worth a toast — the secret is on
      // screen and selectable, which is the fallback.
      toast.error('Could not copy — select the key and copy it manually');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        // Once the secret exists, only the explicit button closes this.
        if (!v && secret) return;
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        title={secret ? 'Copy this key now' : 'New integration key'}
        description={
          secret
            ? undefined
            : 'Give it the least it needs. A key that can only do one thing is a key you can hand out without thinking about it.'
        }
        width="lg"
      >
        {secret ? (
          <KeyRevealed
            secret={secret}
            copied={copied}
            onCopy={() => void copy()}
            onDone={() => {
              setOpen(false);
              reset();
            }}
          />
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              create.mutate();
            }}
          >
            <div className="space-y-3">
              <Field
                label="Name"
                hint="What holds it — “n8n mail assistant”, not “key 2”. It appears in the audit trail against everything this key does."
              >
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="n8n mail assistant"
                  autoFocus
                />
              </Field>

              {presets.length > 0 && (
                <div>
                  <p className="mb-1.5 text-2xs uppercase tracking-wide text-ink-tertiary">
                    Start from
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {presets.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => setChosen(p.capabilities)}
                        title={p.detail}
                        className="rounded-xs border border-line-subtle px-2 py-1 text-2xs text-ink-secondary transition-colors hover:border-brand hover:text-ink"
                      >
                        {p.label}
                        {!p.complete && ' (partial)'}
                      </button>
                    ))}
                  </div>
                  {presets.some((p) => !p.complete) && (
                    <p className="mt-1.5 text-2xs text-ink-tertiary">
                      A preset marked <em>partial</em> includes something you cannot confer, and
                      arrives with that part left out.
                    </p>
                  )}
                </div>
              )}

              <div className="max-h-72 space-y-3 overflow-y-auto rounded-xs border border-line-subtle p-3">
                {groups.map(([group, caps]) => (
                  <div key={group}>
                    <p className="mb-1.5 text-2xs uppercase tracking-wide text-ink-tertiary">
                      {group}
                    </p>
                    <div className="space-y-1.5">
                      {caps.map((c) => (
                        <Checkbox
                          key={c.capability}
                          checked={chosen.includes(c.capability)}
                          onCheckedChange={() => toggle(c.capability)}
                          label={c.label}
                          hint={c.detail}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-2xs text-ink-tertiary">
                {chosen.length === 0
                  ? 'Nothing chosen — a key with no capabilities can do nothing.'
                  : `${chosen.length} chosen.`}
              </p>
            </div>

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="ghost">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                type="submit"
                loading={create.isPending}
                disabled={name.trim().length === 0 || chosen.length === 0}
              >
                Create key
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
